
import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Card, Paper, Divider,
  IconButton, Tooltip, CircularProgress, Alert, Avatar, MenuItem,
  LinearProgress, Stack, Chip, useMediaQuery, useTheme,
} from "@mui/material";
import {
  Edit, Delete, AddCircleOutline, Save,
  FolderOpen as FolderIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const PROJECTS_PER_PAGE = 4;

const STATUS_CONFIG = {
  "Not Started":  { bg: "#F3F6FA", color: "#6B7A8D", border: "#DDE5EE" },
  "In-Progress":  { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A" },
  "Completed":    { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", fontSize: 13,
    "& fieldset": { borderColor: "#E8EDF2" },
    "&:hover fieldset": { borderColor: "#1976d2" },
    "&.Mui-focused fieldset": { borderColor: "#1976d2" },
  },
};

// ── Project card ──────────────────────────────────────────────────────────────
function ProjectCard({ project, onEdit, onDelete, trimDescription }) {
  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG["Not Started"];
  const pct = Number(project.completion) || 0;

  return (
    <Paper elevation={0} sx={{
      mb: 2, borderRadius: "14px", border: "1px solid #E8EDF2", overflow: "hidden",
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": { boxShadow: "0 6px 24px rgba(0,0,0,0.08)", transform: "translateY(-1px)" },
    }}>
      {/* Accent bar based on completion */}
      <Box sx={{ height: 4, bgcolor: pct === 100 ? "#22C55E" : "#1976d2", opacity: 0.8 }} />

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Header row */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
          <Avatar variant="rounded" sx={{
            width: 46, height: 46, bgcolor: "#E8F4FF", color: "#1976d2",
            borderRadius: "10px", flexShrink: 0,
          }}>
            <InsertDriveFileIcon sx={{ fontSize: 24 }} />
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Typography fontWeight={700} fontSize={14} color="#0D1117" noWrap>{project.title}</Typography>
            <Typography fontSize={11} color="#8A9BB0" noWrap>{project.category}</Typography>
          </Box>

          <Stack direction="row" spacing={0.25} flexShrink={0}>
            <IconButton size="small" onClick={() => onEdit(project)}
              sx={{ color: "#1976d2", p: 0.75, borderRadius: "8px", "&:hover": { bgcolor: "#EFF4FF" } }}>
              <Edit sx={{ fontSize: 17 }} />
            </IconButton>
            <Tooltip title="Delete Project">
              <IconButton size="small" onClick={() => onDelete(project._id)}
                sx={{ color: "#EF4444", p: 0.75, borderRadius: "8px", "&:hover": { bgcolor: "#FEF2F2" } }}>
                <Delete sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Status chip + completion */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={0.75}>
          <Chip label={project.status} size="small"
            sx={{ height: 22, fontSize: 11, fontWeight: 700,
              bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
              "& .MuiChip-label": { px: 1 } }} />
          <Typography fontSize={12} fontWeight={700} color={pct === 100 ? "#16A34A" : "#1976d2"}>
            {pct}%
          </Typography>
        </Stack>

        {/* Progress bar */}
        <LinearProgress variant="determinate" value={pct}
          sx={{ height: 7, borderRadius: "4px", bgcolor: "#E8EDF2", mb: 1.5,
            "& .MuiLinearProgress-bar": {
              bgcolor: pct === 100 ? "#22C55E" : "#1976d2",
              borderRadius: "4px",
            },
          }} />

        {/* Description */}
        <Typography fontSize={12} color="#6B7A8D" lineHeight={1.65}>
          {trimDescription(project.description)}
        </Typography>
      </Box>
    </Paper>
  );
}

// ── Project form ──────────────────────────────────────────────────────────────
function ProjectForm({ newProject, onChange, onSubmit, isEditing, loading, error, success, onCancel }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography fontWeight={700} fontSize={15} color="#0D1117">
          {isEditing ? "Edit Project" : "Create a Project"}
        </Typography>
        {isEditing && onCancel && (
          <Button size="small" onClick={onCancel}
            sx={{ textTransform: "none", fontSize: 12, color: "#8A9BB0" }}>
            Cancel
          </Button>
        )}
      </Stack>
      <Divider sx={{ mb: 2.5 }} />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px", fontSize: 13 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: "10px", fontSize: 13 }}>{success}</Alert>}

      <Stack spacing={2}>
        <TextField label="Project Title" name="title" value={newProject.title} onChange={onChange} fullWidth size="small" sx={fieldSx} />
        <TextField label="Category" name="category" value={newProject.category} onChange={onChange} fullWidth size="small" sx={fieldSx} />

        <TextField select label="Status" name="status" value={newProject.status} onChange={onChange} fullWidth size="small" sx={fieldSx}>
          <MenuItem value="Not Started">Not Started</MenuItem>
          <MenuItem value="In-Progress">In-Progress</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
        </TextField>

        <TextField label="Completion (%)" name="completion" type="number" value={newProject.completion}
          onChange={onChange} inputProps={{ min: 0, max: 100 }} fullWidth size="small" sx={fieldSx} />

        <TextField label="Description" name="description" value={newProject.description} onChange={onChange}
          multiline rows={3} fullWidth size="small" sx={fieldSx} />

        <Button variant="contained" size="large"
          startIcon={isEditing ? <Save sx={{ fontSize: 17 }} /> : <AddCircleOutline sx={{ fontSize: 17 }} />}
          onClick={onSubmit} disabled={loading}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: 14,
            bgcolor: "#1976d2", boxShadow: "none",
            "&:hover": { bgcolor: "#1565C0", boxShadow: "none" },
            "&.Mui-disabled": { opacity: 0.6 },
          }}>
          {loading ? "Processing…" : isEditing ? "Save Changes" : "Add Project"}
        </Button>
      </Stack>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const ProjectsManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [newProject, setNewProject] = useState({ title: "", category: "", status: "", completion: "", description: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFormMobile, setShowFormMobile] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id || user?._id;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/projects");
        setProjects((res.data || []).filter((p) => p.userId === userId));
      } catch { setError("Failed to load projects."); }
      finally { setLoadingProjects(false); }
    };
    fetchProjects();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setIsEditing(false); setEditId(null);
    setNewProject({ title: "", category: "", status: "", completion: "", description: "" });
    if (isMobile) setShowFormMobile(false);
  };

  const handleAddProject = async () => {
    if (!newProject.title || !newProject.category || !newProject.status || !newProject.completion || !newProject.description) {
      setError("All fields are required."); return;
    }
    setLoading(true); setError(""); setSuccess("");
    try {
      const res = await axios.post("https://backend-global-1vq9.onrender.com/api/projects",
        { ...newProject, userId, completion: Number(newProject.completion) });
      setProjects((prev) => [res.data, ...prev]);
      setSuccess("Project created successfully!");
      resetForm();
    } catch { setError("Failed to create project."); }
    finally { setLoading(false); }
  };

  const handleEdit = (project) => {
    setIsEditing(true); setEditId(project._id);
    setNewProject({ title: project.title, category: project.category, status: project.status,
      completion: project.completion, description: project.description });
    if (isMobile) { setShowFormMobile(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  const handleUpdateProject = async () => {
    setLoading(true); setError(""); setSuccess("");
    try {
      const payload = { ...newProject, completion: Number(newProject.completion) };
      await axios.put(`https://backend-global-1vq9.onrender.com/api/projects/${editId}`, payload);
      setProjects((prev) => prev.map((p) => (p._id === editId ? { ...p, ...payload } : p)));
      setSuccess("Project updated successfully!");
      resetForm();
    } catch { setError("Failed to update project."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://backend-global-1vq9.onrender.com/api/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p._id !== id));
    } catch { setError("Failed to delete project."); }
  };

  const trimDescription = (text) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length <= 50 ? text : words.slice(0, 50).join(" ") + "…";
  };

  const indexOfLast = currentPage * PROJECTS_PER_PAGE;
  const indexOfFirst = indexOfLast - PROJECTS_PER_PAGE;
  const currentProjects = projects.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(projects.length / PROJECTS_PER_PAGE);

  const formProps = {
    newProject, onChange: handleChange, isEditing, loading, error, success,
    onSubmit: isEditing ? handleUpdateProject : handleAddProject,
    onCancel: resetForm,
  };

  return (
    <Box sx={{ bgcolor: "#F3F6FA", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>

        {/* Page header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography fontWeight={800} color="#0D1117" sx={{ fontSize: { xs: 22, md: 28 }, lineHeight: 1.2 }}>
              My Projects
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Create, track and manage your projects
            </Typography>
          </Box>

          {/* Mobile: toggle form */}
          {isMobile && !showFormMobile && (
            <Button variant="contained" startIcon={<AddCircleOutline sx={{ fontSize: 17 }} />}
              onClick={() => { resetForm(); setShowFormMobile(true); }}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: 13,
                bgcolor: "#1976d2", boxShadow: "none", "&:hover": { bgcolor: "#1565C0", boxShadow: "none" } }}>
              New Project
            </Button>
          )}
        </Stack>

        {/* Mobile form (shown above list) */}
        {isMobile && showFormMobile && (
          <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #E8EDF2", p: 2.5, mb: 3 }}>
            <ProjectForm {...formProps} />
            {!isEditing && (
              <Button fullWidth size="small" onClick={() => setShowFormMobile(false)}
                sx={{ mt: 1.5, textTransform: "none", fontSize: 12, color: "#8A9BB0" }}>
                Cancel
              </Button>
            )}
          </Paper>
        )}

        {/* Two-column layout */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Projects list */}
          <Box flex={1} minWidth={0} order={{ xs: 1, md: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Typography fontWeight={700} fontSize={15} color="#0D1117">
                My Created Projects
              </Typography>
              {!loadingProjects && (
                <Typography variant="caption" color="text.secondary">
                  {projects.length} project{projects.length !== 1 ? "s" : ""}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {loadingProjects ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={40} sx={{ color: "#1976d2" }} />
              </Box>
            ) : currentProjects.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                minHeight={200} bgcolor="#fff" borderRadius="16px" border="1px solid #E8EDF2">
                <FolderIcon sx={{ fontSize: 48, color: "#C5D0DC", mb: 1 }} />
                <Typography fontWeight={600} color="text.secondary" fontSize={14}>No projects yet</Typography>
                <Typography variant="caption" color="text.secondary">Create your first project to get started</Typography>
              </Box>
            ) : (
              <>
                {currentProjects.map((project) => (
                  <ProjectCard key={project._id} project={project}
                    onEdit={handleEdit} onDelete={handleDelete} trimDescription={trimDescription} />
                ))}

                {totalPages > 1 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={2}>
                    <Button variant="outlined" disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
                        borderColor: "#1976d2", color: "#1976d2",
                        "&.Mui-disabled": { borderColor: "#DDE2E8", color: "#B0BEC5" } }}>
                      ← Previous
                    </Button>

                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box key={i} onClick={() => setCurrentPage(i + 1)}
                          sx={{ width: i + 1 === currentPage ? 24 : 8, height: 8, borderRadius: "4px",
                            bgcolor: i + 1 === currentPage ? "#1976d2" : "#C8DAE8",
                            cursor: "pointer", transition: "all 0.22s ease" }} />
                      ))}
                    </Stack>

                    <Button variant="outlined" disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
                        borderColor: "#1976d2", color: "#1976d2",
                        "&.Mui-disabled": { borderColor: "#DDE2E8", color: "#B0BEC5" } }}>
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>

          {/* Desktop form sidebar */}
          {!isMobile && (
            <Paper elevation={0} sx={{
              width: 320, flexShrink: 0, order: 2,
              borderRadius: "16px", border: "1px solid #E8EDF2",
              p: 3, position: "sticky", top: 24,
            }}>
              <ProjectForm {...formProps} />
            </Paper>
          )}
        </Stack>
      </Box>

      <Footer />
    </Box>
  );
};

export default ProjectsManager;

